import { Injectable } from '@angular/core';
import { PersonaResources } from '@core/resources/persona.resources';
import { PersonaTypings } from '@core/typings/persona.typing';
import { AttachYCState, BaseYCService } from '@yourcause/common/state';
import { PersonaQuestionAndAnswersForComponent, PersonaState } from './persona.state';
import { ModalFactory } from '@yourcause/common/modals';
import { AddEditPersonaAnswerCustomResponseModalComponent } from '../../persona/add-edit-persona-answer-custom-response-modal/add-edit-persona-answer-custom-response-modal.component';
import { concatMap, forkJoin, map, Observable } from 'rxjs';
import { AddEditPersonaAnswersModalComponent } from '../../persona/add-edit-persona-answers-modal/add-edit-persona-answers-modal.component';
import { FeatureFlagService } from './feature-flag/feature-flag.service';
import { TokenService } from './token/token.service';
import { AuthService } from './auth.service';

/**
 * Persona Service
 */
@AttachYCState(PersonaState)
@Injectable({ providedIn: 'root' })

export class PersonaService extends BaseYCService<PersonaState> {

  PersonaQAsUser = PersonaTypings.PersonaQuestionAndAnswerTypes.User;
  PersonaQAsNonprofit = PersonaTypings.PersonaQuestionAndAnswerTypes.Nonprofit;
  PersonaQAsStagedNonprofit = PersonaTypings.PersonaQuestionAndAnswerTypes.StagedNonprofit;

  constructor (
    private personaResources: PersonaResources,
    private modalFactory: ModalFactory,
    private featureFlagService: FeatureFlagService,
    private tokenService: TokenService,
    private authService: AuthService
  ) {
    super();
  }

  /**
   * returns personaQuestionsAndAnswersData for the main component
   */
  public get personaQuestionsAndAnswersData (): PersonaQuestionAndAnswersForComponent[] {
    return this.get('personaQuestionsAndAnswersData');
  }

  /**
   * sets the private Getters and then builds the data for personaQuestionsAndAnswersData
   *
   * @param personaServiceDetails; dictates which API endpoints are used within the resource file along with which getters to set/use when building data for the component
   */
  public setPersonaAnswers (
    personaServiceDetails: PersonaTypings.PersonaServiceDetails
  ){
    let personaQuestionAndAnswerType = this.setPersonaQuestionAndAnswerType(personaServiceDetails);
    let specificPersonaUserAnswers = this.getSpecificPersonaUserAnswers(personaServiceDetails);

    let personaQuestionsAndAnswers = this.personaResources.getPersonaQuestionsAndAnswers();

    var handlePersonaQuestionAndAnswersData: PersonaTypings.HandlePersonaQuestionAndAnswersData = {
      personaQuestionAndAnswerType: personaQuestionAndAnswerType,
      personaServiceDetails: personaServiceDetails,
      personaQuestionsAndAnswers: personaQuestionsAndAnswers,
      personaUserAnswersOne: specificPersonaUserAnswers[0],
      personaUserAnswersTwo: specificPersonaUserAnswers?.length == 2 ? specificPersonaUserAnswers[1] : null
    };

    if (specificPersonaUserAnswers?.length == 2) {
      this.setPersonaQuestionsAndAnswers_FetchMultipleResponses(handlePersonaQuestionAndAnswersData);
    } else if (specificPersonaUserAnswers?.length == 1) {
      this.setPersonaQuestionsAndAnswers_FetchSingeResponse(handlePersonaQuestionAndAnswersData);
    }
  }

  /**
   * Private Getters - used to set personaQuestionsAndAnswers and personaUserAnswers within personaQuestionsAndAnswersData
   */

  /**
   * returns personaQuestionsAndAnswers; data on each question/answer(s) groups
   */
  private get personaQuestionsAndAnswers (): PersonaTypings.GetPersonaQuestionsAndAnswersQueryDto {
    return this.get('personaQuestionsAndAnswers');
  }

  /**
   * returns personaUserAnswers; data on each answer(s) a user has for a question at the User level
   */
  private get personaUserAnswers (): PersonaTypings.GetPersonaUserAnswersQueryDto {
    return this.get('personaUserAnswers');
  }

  /**
   * returns personaUserNonprofitAnswers; data on each answer(s) a user has for a question at the Nonprofit level
   */
  private get personaUserNonprofitAnswers (): PersonaTypings.GetPersonaUserNonprofitAnswersQueryDto {
    return this.get('personaUserNonprofitAnswers');
  }

  /**
   * returns personaUserStagedNonprofitAnswers; data on each answer(s) a user has for a question at the Staged Nonprofit level
   */
  private get personaUserStagedNonprofitAnswers (): PersonaTypings.GetPersonaUserStagedNonprofitAnswersQueryDto {
    return this.get('personaUserStagedNonprofitAnswers');
  }

  /**
   * returns personaUserNonprofitAnswersForReview; data on each answer(s) a user has for a question at the Nonprofit level; being retrieved by a reviewer
   */
  private get personaUserNonprofitAnswersForReview (): PersonaTypings.GetPersonaUserNonprofitAnswersForReviewQueryDto {
    return this.get('personaUserNonprofitAnswersForReview');
  }

  /**
   * returns personaUserStagedNonprofitAnswersForReview; data on each answer(s) a user has for a question at the Staged Nonprofit level; being retrieved by a reviewer
   */
  private get personaUserStagedNonprofitAnswersForReview (): PersonaTypings.GetPersonaUserStagedNonprofitAnswersForReviewQueryDto {
    return this.get('personaUserStagedNonprofitAnswersForReview');
  }

  /**
   * Determines Persona Question and Answer Type based on incoming details
   * @param personaServiceDetails; details to determine PersonaQuestionAndAnswerTypes
   * @returns PersonaQuestionAndAnswerTypes
   */
  private setPersonaQuestionAndAnswerType (
    personaServiceDetails: PersonaTypings.PersonaServiceDetails
  ): PersonaTypings.PersonaQuestionAndAnswerTypes {
    return personaServiceDetails.nonprofitId ? this.PersonaQAsNonprofit :
      personaServiceDetails.stagedNonprofitId ? this.PersonaQAsStagedNonprofit :
      this.PersonaQAsUser;
  }

  /**
   * returns the observables of the answers to use when building the component
   * @param personaServiceDetails; dictates which answers to return
   * @returns may either return one set of answers or two sets if we are to default to another answer set within an area of the application
   */
  private getSpecificPersonaUserAnswers (personaServiceDetails: PersonaTypings.PersonaServiceDetails) {
    let nonprofitId = personaServiceDetails.nonprofitId;
    let stagedNonprofitId = personaServiceDetails.stagedNonprofitId;
    let userId = personaServiceDetails.userId;
    let nonprofitAdminRequestId = personaServiceDetails.nonprofitAdminRequestId;
    let beingReviewed = personaServiceDetails.beingReviewed;
    let unansweredQuestionsOnly = personaServiceDetails.unansweredQuestionsOnly;

    if (!(nonprofitId || stagedNonprofitId || userId || nonprofitAdminRequestId)) {
      //  MyAccount; User level
      //  Unanswered; User level
      return [this.personaResources.getPersonaUserAnswers()];
    } else {
      if (nonprofitId) {
        if (userId && nonprofitAdminRequestId && beingReviewed) {
          //  Nonprofit Admin Request; Nonprofit level for reviewer
          return [
            this.personaResources.getPersonaUserNonprofitAnswersForReview(userId, nonprofitId, nonprofitAdminRequestId)
          ];
        } else if (unansweredQuestionsOnly) {
          //  Unanswered; Nonprofit level
          return [
            this.personaResources.getPersonaUserNonprofitAnswers(nonprofitId)
          ];
        } else {
          //  MyNonprofits; Nonprofit level and User level
          //  Nonprofit Admin Request; Nonprofit level and User level
          return [
            this.personaResources.getPersonaUserNonprofitAnswers(nonprofitId),
            this.personaResources.getPersonaUserAnswers()
          ];
        }
      } else if (stagedNonprofitId) {
        if (userId && nonprofitAdminRequestId && beingReviewed) {
          //  Nonprofit Admin Request; Staged Nonprofit level for reviewer
          return [this.personaResources.getPersonaUserStagedNonprofitAnswersForReview(userId, stagedNonprofitId, nonprofitAdminRequestId)];
        } else {
          //  Nonprofit Admin Request; Staged Nonprofit level and User level
          return [
            this.personaResources.getPersonaUserStagedNonprofitAnswers(stagedNonprofitId),
            this.personaResources.getPersonaUserAnswers()
          ];
        }
      }
    }

    return null;
  }

  /**
   * processes the questions and answers and the user's multiple responses for them; for when an area defaults to another level for answers (only defaulting to the user level in MyNonprofits, Nonprofit Admin Requests)
   * @param handlePersonaQuestionAndAnswersData; the specific question and answers along with the user responses to process for the component.
   */
  private setPersonaQuestionsAndAnswers_FetchMultipleResponses (
    handlePersonaQuestionAndAnswersData: PersonaTypings.HandlePersonaQuestionAndAnswersData
  ) {
    let nonprofitStagedNonprofitAnswers:
      PersonaTypings.GetPersonaUserNonprofitAnswersQueryDto |
      PersonaTypings.GetPersonaUserStagedNonprofitAnswersQueryDto = null;

    handlePersonaQuestionAndAnswersData.
      personaQuestionsAndAnswers
        .pipe(
          concatMap(questionsAndAnswers => {
            this.set('personaQuestionsAndAnswers', questionsAndAnswers);

            return handlePersonaQuestionAndAnswersData.personaUserAnswersOne;
          }),
          concatMap(personaNonprofitStagedNonprofitAnswers => {
            nonprofitStagedNonprofitAnswers =
              handlePersonaQuestionAndAnswersData.personaQuestionAndAnswerType === this.PersonaQAsNonprofit ?
            personaNonprofitStagedNonprofitAnswers as PersonaTypings.GetPersonaUserNonprofitAnswersQueryDto :
            personaNonprofitStagedNonprofitAnswers as PersonaTypings.GetPersonaUserStagedNonprofitAnswersQueryDto;

            return handlePersonaQuestionAndAnswersData.personaUserAnswersTwo;
          })
        ).subscribe(personaUserAnswers => {
          var usersAnswers = personaUserAnswers as PersonaTypings.GetPersonaUserAnswersQueryDto;

          this.setUserPersonaAnswers(handlePersonaQuestionAndAnswersData.personaQuestionAndAnswerType, handlePersonaQuestionAndAnswersData.personaServiceDetails, usersAnswers, nonprofitStagedNonprofitAnswers);
        });
  }

  /**
   * processes the questions and answers and the user's single responses for them
   * @param handlePersonaQuestionAndAnswersData; the specific question and answers along with the user response to process for the component.
   */
  private setPersonaQuestionsAndAnswers_FetchSingeResponse (
    handlePersonaQuestionAndAnswersData: PersonaTypings.HandlePersonaQuestionAndAnswersData
  ) {
    let usersNonprofitStagedNonprofitPersonaAnswers: PersonaTypings.GetPersonaUserNonprofitAnswersQueryDto | PersonaTypings.GetPersonaUserNonprofitAnswersForReviewQueryDto | PersonaTypings.GetPersonaUserStagedNonprofitAnswersForReviewQueryDto = null;

    handlePersonaQuestionAndAnswersData.
      personaQuestionsAndAnswers
        .pipe(
          concatMap(questionsAndAnswers => {
            this.set('personaQuestionsAndAnswers', questionsAndAnswers);

            return handlePersonaQuestionAndAnswersData.personaUserAnswersOne;
          })
        ).subscribe(personaAnswers => {
          if (handlePersonaQuestionAndAnswersData.personaQuestionAndAnswerType === this.PersonaQAsNonprofit) {
            if (!handlePersonaQuestionAndAnswersData.personaServiceDetails.unansweredQuestionsOnly) {
              usersNonprofitStagedNonprofitPersonaAnswers = personaAnswers as PersonaTypings.GetPersonaUserNonprofitAnswersForReviewQueryDto;
            } else {
              usersNonprofitStagedNonprofitPersonaAnswers = personaAnswers as PersonaTypings.GetPersonaUserNonprofitAnswersQueryDto;
            }

            this.setUserPersonaAnswers(handlePersonaQuestionAndAnswersData.personaQuestionAndAnswerType, handlePersonaQuestionAndAnswersData.personaServiceDetails, null, usersNonprofitStagedNonprofitPersonaAnswers);
          } else if (handlePersonaQuestionAndAnswersData.personaQuestionAndAnswerType === this.PersonaQAsStagedNonprofit) {
            usersNonprofitStagedNonprofitPersonaAnswers = personaAnswers as PersonaTypings.GetPersonaUserStagedNonprofitAnswersForReviewQueryDto;

            this.setUserPersonaAnswers(handlePersonaQuestionAndAnswersData.personaQuestionAndAnswerType, handlePersonaQuestionAndAnswersData.personaServiceDetails, null, usersNonprofitStagedNonprofitPersonaAnswers);
          } else {
            let usersPersonaAnswers = personaAnswers as PersonaTypings.GetPersonaUserAnswersQueryDto;

            this.setUserPersonaAnswers(handlePersonaQuestionAndAnswersData.personaQuestionAndAnswerType, handlePersonaQuestionAndAnswersData.personaServiceDetails, usersPersonaAnswers, null);
          }
        });
  }

  /**
   * subscribes to the questions and answers and the user's responses for them
   * @param handlePersonaQuestionAndAnswersData; the specific question and answers along with the user response to process for the component.
   * @param personaQuestionAndAnswerType; the specific question and answers type/level
   * @param personaServiceDetails; details on the use case for the component
   * @param personaUsersAnswers; user responses
   * @param personaUsersNonprofitBasedAnswers; user nonprofit/staged nonprofit responses
   */
  private setUserPersonaAnswers (
    personaQuestionAndAnswerType: PersonaTypings.PersonaQuestionAndAnswerTypes,
    personaServiceDetails: PersonaTypings.PersonaServiceDetails,
    personaUsersAnswers?: PersonaTypings.GetPersonaUserAnswersQueryDto,
    personaUsersNonprofitBasedAnswers?: PersonaTypings.GetPersonaUserNonprofitAnswersForReviewQueryDto
      | PersonaTypings.GetPersonaUserNonprofitAnswersQueryDto
      | PersonaTypings.GetPersonaUserStagedNonprofitAnswersForReviewQueryDto
      | PersonaTypings.GetPersonaUserStagedNonprofitAnswersQueryDto
  ) {
    let nonprofitId = personaServiceDetails.nonprofitId;
    let stagedNonprofitId = personaServiceDetails.stagedNonprofitId;
    let userId = personaServiceDetails.userId;
    let nonprofitAdminRequestId = personaServiceDetails.nonprofitAdminRequestId;
    let beingReviewed = personaServiceDetails.beingReviewed;
    let unansweredQuestionsOnly = personaServiceDetails.unansweredQuestionsOnly;

    if (!(nonprofitId || stagedNonprofitId || userId || nonprofitAdminRequestId)) {
      //  MyAccount; User level
      //  Unanswered; User level
      this.set('personaUserAnswers', personaUsersAnswers)
    } else {
      if (nonprofitId) {
        if (userId && nonprofitAdminRequestId && beingReviewed) {
          //  Nonprofit Admin Request; Nonprofit level for reviewer
          this.set('personaUserNonprofitAnswersForReview', personaUsersNonprofitBasedAnswers as PersonaTypings.GetPersonaUserNonprofitAnswersForReviewQueryDto);
        } else if (unansweredQuestionsOnly) {
          //  Unanswered; Nonprofit level
          this.set('personaUserNonprofitAnswers', personaUsersNonprofitBasedAnswers as PersonaTypings.GetPersonaUserNonprofitAnswersQueryDto);
        } else {
          //  MyNonprofits; Nonprofit level and User level
          //  Nonprofit Admin Request; Nonprofit level and User level
          this.set('personaUserNonprofitAnswers', personaUsersNonprofitBasedAnswers as PersonaTypings.GetPersonaUserNonprofitAnswersQueryDto);

          this.set('personaUserAnswers', personaUsersAnswers)
        }
      } else if (stagedNonprofitId) {
        if (userId && nonprofitAdminRequestId && beingReviewed) {
          //  Nonprofit Admin Request; Nonprofit Staged level for reviewer
          this.set('personaUserStagedNonprofitAnswersForReview', personaUsersNonprofitBasedAnswers as PersonaTypings.GetPersonaUserStagedNonprofitAnswersForReviewQueryDto);
        } else {
          //  Nonprofit Admin Request; Staged Nonprofit level and User level
          this.set('personaUserStagedNonprofitAnswers', personaUsersNonprofitBasedAnswers as PersonaTypings.GetPersonaUserStagedNonprofitAnswersQueryDto);

          this.set('personaUserAnswers', personaUsersAnswers)
        }
      }
    }

    //  set the data for the component
    this.setPersonaQuestionsAndAnswersData(personaQuestionAndAnswerType, beingReviewed, unansweredQuestionsOnly);
  }

  /**
   *
   * Edit a custom response to a question or add if one does not exist.
   *
   * @param personaUserAnswers
   * @param systemAnswerId
   * @param userSystemAnswerId
   * @param personaQuestionAndAnswerTypeWithDetails
   * @param customResponse
   */
  public async editCustomResponsePersonaUserAnswer (
    personaUserAnswers: PersonaTypings.PersonaUserAnswers
      | PersonaTypings.PersonaUserNonprofitAnswers
      | PersonaTypings.PersonaUserStagedNonprofitAnswers
      | PersonaTypings.PersonaUserNonprofitAnswersForReview
      | PersonaTypings.PersonaUserStagedNonprofitAnswersForReview,
    systemAnswerId: number,
    userSystemAnswerId: number,
    personaQuestionAndAnswerTypeWithDetails: PersonaTypings.PersonaQuestionAndAnswerTypeWithDetails,
    customResponse?: string
  ) {
    let questionAndAnswerThatAllowsCustomResponse =
      this.personaQuestionsAndAnswersData
        ?.find(x => x?.personaQuestionsAndAnswers?.systemAnswers?.find(x => x.systemAnswerId == systemAnswerId));

    if (questionAndAnswerThatAllowsCustomResponse) {
      const customResponseForQuestion = await this.modalFactory.open(
        AddEditPersonaAnswerCustomResponseModalComponent,
        {
          systemQuestion: questionAndAnswerThatAllowsCustomResponse?.personaQuestionsAndAnswers?.translationText,
          systemAnswer: questionAndAnswerThatAllowsCustomResponse?.personaQuestionsAndAnswers?.systemAnswers?.find(x => x.systemAnswerId == systemAnswerId)?.translationText,
          customResponse
        },
        {
          class: 'modal-lg'
        }
      );

      if (!!customResponseForQuestion) {
        if (userSystemAnswerId) {
          this.onAnswerUpdate_PUT(userSystemAnswerId, systemAnswerId, questionAndAnswerThatAllowsCustomResponse.personaQuestionsAndAnswers, personaQuestionAndAnswerTypeWithDetails, null, customResponseForQuestion, personaUserAnswers);
        } else {
          this.editCustomResponsePersonaUserAnswer_OnAnswerUpdate_POST(questionAndAnswerThatAllowsCustomResponse.personaQuestionsAndAnswers.systemQuestionId, systemAnswerId, personaQuestionAndAnswerTypeWithDetails, customResponseForQuestion, personaUserAnswers);
        }
      }
    }
  }

  /**
   *
   * Handles when an answer is supplied to a question.
   *
   * @param response
   * @param personaQuestionsAndAnswers
   * @param personaUserAnswers
   * @param personaQuestionAndAnswerTypeWithDetails
   * @param allowEmptySelection
   */
  public async onPersonaAnswerUpdate (
    response: number | number[],
    personaQuestionsAndAnswers: PersonaTypings.PersonaQuestionAndAnswers,
    personaUserAnswers: PersonaTypings.PersonaUserAnswers | PersonaTypings.PersonaUserNonprofitAnswers | PersonaTypings.PersonaUserStagedNonprofitAnswers | PersonaTypings.PersonaUserNonprofitAnswersForReview | PersonaTypings.PersonaUserStagedNonprofitAnswersForReview,
    personaQuestionAndAnswerTypeWithDetails: PersonaTypings.PersonaQuestionAndAnswerTypeWithDetails,
    allowEmptySelection: boolean = false
  ) {
    if (personaQuestionsAndAnswers) {
      if (!personaQuestionsAndAnswers.allowsMultipleResponses) {
        await this.onAnswerUpdate_SingleResponseQuestion(
          response as number,
          personaQuestionsAndAnswers,
          personaUserAnswers,
          personaQuestionAndAnswerTypeWithDetails,
          allowEmptySelection
        );
      } else if (Array.isArray(response) && personaQuestionsAndAnswers.allowsMultipleResponses) {
        await this.onAnswerUpdate_MultiResponseQuestion(
          response,
          personaQuestionsAndAnswers,
          personaUserAnswers,
          personaQuestionAndAnswerTypeWithDetails,
          allowEmptySelection
        );
      }
    }
  }

  /**
   * gets the unanswered persona questions for a user at the User level
   * @returns
   */
  public async getUnansweredPersonaQuestionsForUserModalForSigningIn () {
    // determine if this is a new user signing in for the first time
    const newUser = this.authService?.signInFlow?.existing === false;

    if (!this.tokenService.tokenInfo) {
      await this.tokenService.fetchUserClaims();
    }

    if (this.featureFlagService.systemQuestionsFlagValue === undefined) {
      await this.featureFlagService.getAllFeatureFlagsLookup();
    }

    var answeredAllQuestions = true;

    if (this.tokenService.tokenInfo
        && this.featureFlagService.systemQuestionsFlagValue
        && (this.tokenService.tokenInfo?.unansweredPersonaQuestions ?? false)) {
          var answeredAllQuestions = await this.getUnansweredPersonaQuestionsModal(newUser);
    }

    return answeredAllQuestions;
  }

  /**
   * returns whether or not a user answers any unanswered questions they have to answer; can be set for a specific nonprofit that a user administers.
   * @param nonprofitId
   * @param nonprofitName
   * @returns
   */
  private async getUnansweredPersonaQuestionsModal (newUser: boolean = false) {
    const answered = await this.modalFactory.open(
      AddEditPersonaAnswersModalComponent,
      {
        personaQuestionAndAnswerType: this.PersonaQAsUser,
        unansweredQuestionsOnly: true,
        newUser: newUser
      },
      { keyboard: false, class: 'modal-lg' }
    );

    var updatedTokenInfo = {
      ...this.tokenService.tokenInfo,
      unansweredPersonaQuestions: !answered
    };

    this.tokenService.setTokenInfo(updatedTokenInfo);

    return answered;
  }

  /**
   * checks to see if the incoming array of answers differs from the existing array of answers.
   * @param incomingAnswerIds
   * @param existingAnswerIds
   * @returns
   */
  public haveMultiResponseQuestionAnswersChanged (incomingAnswerIds: number[], existingAnswerIds: number[]) {
    if (incomingAnswerIds.length !== existingAnswerIds.length) {
      return true;
    }

    const sortedArr1 = [...incomingAnswerIds].sort((a, b) => a - b);
    const sortedArr2 = [...existingAnswerIds].sort((a, b) => a - b);

    return !sortedArr1.every((value, index) => value === sortedArr2[index]);
  }

  /**
   *
   * Adds answers for a user to a Nonprofit Admin Request when the request is saved/updated. This ensure answers are brought from the User level to the Nonprofit/Staged Nonprofit level for the user and keeps the answers the user has in sync.
   *
   * @param personaQuestionAndAnswerTypeWithDetails
   */
  public addUserSystemAnswersForNonprofitAdminRequest (
    personaQuestionAndAnswerTypeWithDetails: PersonaTypings.PersonaQuestionAndAnswerTypeWithDetails
  ) {
    if (personaQuestionAndAnswerTypeWithDetails.personaQuestionAndAnswerType === this.PersonaQAsNonprofit
        || personaQuestionAndAnswerTypeWithDetails.personaQuestionAndAnswerType === this.PersonaQAsStagedNonprofit) {
      const answersToAdd: {
        [key: number]: Observable<{ userSystemAnswerId: PersonaTypings.NewPersonaAnswer; systemAnswerId: number; }>[]
      } = {};

      this.personaQuestionsAndAnswersData
        ?.forEach(x => {
          const systemQuestionId = x.personaQuestionsAndAnswers.systemQuestionId;

          const newAnswers:
            Observable<{ userSystemAnswerId: PersonaTypings.NewPersonaAnswer; systemAnswerId: number; }>[] = [];

          const addAnswer = (systemAnswerId: number, userSystemAnswerCustomResponse: any) => {
            newAnswers.push(this.personaResources.upsertPersonaUserAnswer(systemAnswerId, personaQuestionAndAnswerTypeWithDetails, userSystemAnswerCustomResponse));
          };

        const processAnswers = (answers: any[]) => {
          answers.forEach(answer => {
            if (!answer.userSystemAnswerId) {
              addAnswer(answer.systemAnswerId, answer.userSystemAnswerCustomResponse ?? null);
            }
          });
        };

        if (x.personaQuestionsAndAnswers.allowsMultipleResponses) {
          if (x?.personaUserAnswers?.systemAnswers?.length > 0) {
            processAnswers(x.personaUserAnswers.systemAnswers);
          } else {
            addAnswer(x.personaQuestionsAndAnswers.systemAnswers[0].systemAnswerId, null);
          }
        } else {
          if (x?.personaUserAnswers?.systemAnswers?.length > 0) {
            const answer = x.personaUserAnswers.systemAnswers[0];

            if (!answer.userSystemAnswerId) {
              addAnswer(answer.systemAnswerId, answer.userSystemAnswerCustomResponse ?? null);
            }
          } else {
            addAnswer(x.personaQuestionsAndAnswers.systemAnswers[0].systemAnswerId, null);
          }
        }

        if (newAnswers.length > 0) {
          answersToAdd[systemQuestionId] = newAnswers;
        }
      });

      const observablesWithKeys = Object.entries(answersToAdd).map(([key, observables]) =>
        forkJoin(observables).pipe(
          map(results => ({ systemQuestionId: Number(key), results }))
        )
      );

      forkJoin(observablesWithKeys).subscribe();
    }
  }

  /**
   * method updates personaQuestionsAndAnswersData state data so changes/answers can be reflected in the component that subscribes to personaQuestionsAndAnswersData
   * @param updates
   */
  private updatePersonaQuestionsAndAnswersData (
    updates: PersonaTypings.UpdatePersonaQuestionsAndAnswersDataInfo
  ) {
    let systemQuestionId = updates.systemQuestionId;
    let systemAnswerInfos = updates.systemAnswerInfos;
    let defaultIfNoAnswer = false;
    let customResponse = updates.customResponse;
    let userSystemAnswerIdForCustomResponse = updates.userSystemAnswerIdForCustomResponse;
    let systemAnswerIdForCustomResponse = updates.systemAnswerIdForCustomResponse;

    //  grab the component information for the specific question if it exist
    let questionAndAnswersData = this.personaQuestionsAndAnswersData
      ?.find(x => x.personaQuestionsAndAnswers.systemQuestionId == systemQuestionId);

    //  array to hold all the user system answer ids we have for the user
    let userSystemAnswerIds: number[] = [];

    //  if it does exist for the question
    if (questionAndAnswersData) {
      // rebuild the user's answers to the question for the component state
      questionAndAnswersData.personaUserAnswers = {
        systemAnswers: undefined,
        systemQuestionId: systemQuestionId
      }

      //  used to hold the updated data we are rebuilding
      var updatedSystemAnswersForComponent = [];

      //  if these are being provided, then the user has answers or the state of the control has a default value
      if (systemAnswerInfos && Object.keys(systemAnswerInfos).length > 0) {
        for (let key in systemAnswerInfos) {
          var systemAnswerId = systemAnswerInfos[+key];
          var userSystemAnswerId = +key;

          updatedSystemAnswersForComponent.push({
            systemAnswerId,
            userSystemAnswerId,
            userSystemAnswerCustomResponse: null
          });

          userSystemAnswerIds.push(userSystemAnswerId);
        }

        questionAndAnswersData.personaUserAnswers.systemAnswers = updatedSystemAnswersForComponent;
      } else {
        if (defaultIfNoAnswer) {
          questionAndAnswersData.personaUserAnswers.systemAnswers = [
            {
              systemAnswerId: questionAndAnswersData.personaQuestionsAndAnswers.systemAnswers[0].systemAnswerId,
              userSystemAnswerId: null,
              userSystemAnswerCustomResponse: null
            }
          ];
        } else {
          questionAndAnswersData.personaUserAnswers.systemAnswers = [];
        }
      }

      // rebuild the user's custom response answer details to the question for the component state
      questionAndAnswersData.customResponseForQuestion = customResponse;
      questionAndAnswersData.userSystemAnswerIdForCustomResponse = userSystemAnswerIdForCustomResponse;
      questionAndAnswersData.systemAnswerIdForCustomResponse = systemAnswerIdForCustomResponse;

      // rebuild the user's answers to the question for the component state
      questionAndAnswersData.userSystemAnswerIds = userSystemAnswerIds;

      this.set('personaQuestionsAndAnswersData', this.personaQuestionsAndAnswersData);
    }
  }

  /**
   * sets the data for personaQuestionsAndAnswersData
   * @param personaQuestionAndAnswerType
   * @param beingReviewed
   * @param unansweredQuestionsOnly
   */
  private setPersonaQuestionsAndAnswersData (
    personaQuestionAndAnswerType: PersonaTypings.PersonaQuestionAndAnswerTypes,
    beingReviewed: boolean,
    unansweredQuestionsOnly: boolean = false
  ) {
    let componentData = this.personaQuestionsAndAnswers?.personaQuestionsAndAnswers?.map((questionAndAnswers) => {
      //  Persona Question Details
      var questionId = questionAndAnswers.systemQuestionId; //  Id

      let usersAnswersToTheQuestion: PersonaTypings.PersonaUserAnswers | PersonaTypings.PersonaUserNonprofitAnswers | PersonaTypings.PersonaUserStagedNonprofitAnswers | PersonaTypings.PersonaUserNonprofitAnswersForReview | PersonaTypings.PersonaUserStagedNonprofitAnswersForReview = null;

      //  Users Answers to Persona Question
      switch (personaQuestionAndAnswerType) {
        case this.PersonaQAsUser:
          usersAnswersToTheQuestion = this.personaUserAnswers?.personaUserAnswers?.find(x => x.systemQuestionId == questionId);

          break;
        case this.PersonaQAsNonprofit:
          usersAnswersToTheQuestion = !beingReviewed ?
            this.personaUserNonprofitAnswers?.personaUserNonprofitAnswers?.find(x => x.systemQuestionId == questionId) :
            this.personaUserNonprofitAnswersForReview?.personaUserNonprofitAnswersForReview?.find(x => x.systemQuestionId == questionId);

          break;
        case this.PersonaQAsStagedNonprofit:
          usersAnswersToTheQuestion = !beingReviewed ?
            this.personaUserStagedNonprofitAnswers?.personaUserStagedNonprofitAnswers?.find(x => x.systemQuestionId == questionId) :
            this.personaUserStagedNonprofitAnswersForReview?.personaUserStagedNonprofitAnswersForReview?.find(x => x.systemQuestionId == questionId);

          break;
        default:
          //  If component is used, PersonaQuestionAndAnswerType must be provided
          console.error('Provided PersonaQuestionAndAnswerType is not valid.')

          throw ("Provided PersonaQuestionAndAnswerType is not valid.");
      }

      //  A nonprofit admin request will have either type Nonprofit or type StagedNonprofit
      //  If a nonprofit admin request is being created/updated, then it is not beingReviewed and it is not viewOnly
      //
      //  If the user has no answers to the StagedNonprofit or the Nonprofit, we pull answers from their user level
      if ((personaQuestionAndAnswerType === this.PersonaQAsNonprofit
        || personaQuestionAndAnswerType === this.PersonaQAsStagedNonprofit
      ) && !usersAnswersToTheQuestion && !beingReviewed && !unansweredQuestionsOnly) {
        var personaUserAnswers = this.personaUserAnswers
          ?.personaUserAnswers
          ?.filter(x => x.systemQuestionId == questionId);

        if (personaUserAnswers?.length > 0) {
          if (personaQuestionAndAnswerType === this.PersonaQAsNonprofit) {
            var usersAnswersForUserNonprofitAnswers: PersonaTypings.PersonaUserNonprofitAnswerSystemQuestionAnswers[] = [];

            personaUserAnswers[0]?.systemAnswers?.forEach(x => {
              usersAnswersForUserNonprofitAnswers.push({
                systemAnswerId: x.systemAnswerId,
                userSystemAnswerId: null,
                userSystemAnswerCustomResponse: x.userSystemAnswerCustomResponse
              });
            })

            usersAnswersToTheQuestion = {
              systemQuestionId: questionId,
              systemAnswers: usersAnswersForUserNonprofitAnswers
            };
          } else {
            var usersAnswersForUserStagedNonprofitAnswers: PersonaTypings.PersonaUserStagedNonprofitAnswerSystemQuestionAnswers[] = [];

            personaUserAnswers[0]?.systemAnswers?.forEach(x => {
              usersAnswersForUserStagedNonprofitAnswers.push({
                systemAnswerId: x.systemAnswerId,
                userSystemAnswerId: null,
                userSystemAnswerCustomResponse: x.userSystemAnswerCustomResponse
              });
            })

            usersAnswersToTheQuestion = {
              systemQuestionId: questionId,
              systemAnswers: usersAnswersForUserStagedNonprofitAnswers
            };
          }
        }
      }

      let [
        userSystemAnswerIdForCustomResponse,
        systemAnswerIdForCustomResponse,
        customResponseForQuestion,
        userSystemAnswerIds
      ] = this.buildPersonaQuestionAndAnswers_AnswerDetails(usersAnswersToTheQuestion, questionAndAnswers);

      return {
        personaQuestionsAndAnswers: questionAndAnswers,
        personaUserAnswers: usersAnswersToTheQuestion,
        formFieldName: `systemQuestion${questionAndAnswers.systemQuestionId}`,
        formFieldSelectOptions:
          questionAndAnswers.systemAnswers.map((answers) => {

            return {
              label: answers.translationText,
              value: answers.systemAnswerId
            }
          }),
        userSystemAnswerIdForCustomResponse,
        systemAnswerIdForCustomResponse,
        customResponseForQuestion,
        userSystemAnswerIds
      }
    });

    //  If unanswered questions only, we don't care to continue if answered
    if (unansweredQuestionsOnly) {
      componentData = componentData.filter(x => x.userSystemAnswerIds.length == 0);
    }

    this.set('personaQuestionsAndAnswersData', componentData)
  }

  /**
   * fetches information about an answer to a given question
   * @param userAnswersToQuestion
   * @param questionAndAnswers
   * @returns
   */
  private buildPersonaQuestionAndAnswers_AnswerDetails (
    userAnswersToQuestion: PersonaTypings.PersonaUserAnswers
      | PersonaTypings.PersonaUserNonprofitAnswers
      | PersonaTypings.PersonaUserStagedNonprofitAnswers
      | PersonaTypings.PersonaUserNonprofitAnswersForReview
      | PersonaTypings.PersonaUserStagedNonprofitAnswersForReview,
    questionAndAnswers: PersonaTypings.PersonaQuestionAndAnswers
  ): [
      number,
      number,
      string,
      number[]
    ] {
    let userSystemAnswerIdsForQuestion: number[] = [];
    let userSystemAnswerIdForCustomResponseAnswer = null;
    let systemAnswerIdForCustomResponseAnswer = null;
    let userSystemAnswerCustomResponseForCustomResponseAnswer = null;

    //  Get info on custom response answer
    let customResponseSystemAnswerId = questionAndAnswers?.systemAnswers?.find(x => x.isCustomResponse)?.systemAnswerId;

    if (userAnswersToQuestion?.systemAnswers) {
      //  Build answer info
      userAnswersToQuestion.systemAnswers.forEach(systemAnswer => {
        //  if answer is custom response
        if (systemAnswer.systemAnswerId == customResponseSystemAnswerId) {
          userSystemAnswerIdForCustomResponseAnswer = systemAnswer.userSystemAnswerId;
          userSystemAnswerCustomResponseForCustomResponseAnswer = systemAnswer.userSystemAnswerCustomResponse;
        }

        userSystemAnswerIdsForQuestion.push(systemAnswer.userSystemAnswerId);
      });
    }

    systemAnswerIdForCustomResponseAnswer = customResponseSystemAnswerId;

    return [
      userSystemAnswerIdForCustomResponseAnswer,
      systemAnswerIdForCustomResponseAnswer,
      userSystemAnswerCustomResponseForCustomResponseAnswer,
      userSystemAnswerIdsForQuestion
    ];
  }

  /**
   * clears the data of the component
   */
  public clearComponentData () {
    this.set('personaQuestionsAndAnswersData', null);
  }

  private async addCustomResponsePersonaUserAnswer_ForMultiResponse (
    systemAnswerId: number,
    personaQuestionAndAnswerTypeWithDetails: PersonaTypings.PersonaQuestionAndAnswerTypeWithDetails
  ): Promise<[string, Observable<{ userSystemAnswerId: PersonaTypings.NewPersonaAnswer; systemAnswerId: number; }>]> | null {
    //  question and answer that allow custom response
    let questionAndAnswerThatAllowsCustomResponse = this.personaQuestionsAndAnswersData
      .find(x => x.personaQuestionsAndAnswers.systemAnswers.find(x => x.systemAnswerId == systemAnswerId));

    //  modal to allow user to provide a custom response
    const modalResponse = await this.modalFactory.open(
      AddEditPersonaAnswerCustomResponseModalComponent,
      {
        systemQuestion: questionAndAnswerThatAllowsCustomResponse.personaQuestionsAndAnswers.translationText,
        systemAnswer: questionAndAnswerThatAllowsCustomResponse.personaQuestionsAndAnswers.systemAnswers.find(x => x.systemAnswerId == systemAnswerId).translationText
      },
      {
        class: 'modal-lg'
      }
    );

    //  if user provides an answer
    if (!!modalResponse) {

      return [modalResponse, this.personaResources.upsertPersonaUserAnswer(systemAnswerId, personaQuestionAndAnswerTypeWithDetails, modalResponse)];
    } else {
      return null;
    }
  }

  private editCustomResponsePersonaUserAnswer_OnAnswerUpdate_POST (
    systemQuestionId: number,
    systemAnswerId: number,
    personaQuestionAndAnswerTypeWithDetails: PersonaTypings.PersonaQuestionAndAnswerTypeWithDetails,
    modalResponse: string = null,
    personaUserAnswers: PersonaTypings.PersonaUserAnswers
      | PersonaTypings.PersonaUserNonprofitAnswers
      | PersonaTypings.PersonaUserStagedNonprofitAnswers
      | PersonaTypings.PersonaUserNonprofitAnswersForReview
      | PersonaTypings.PersonaUserStagedNonprofitAnswersForReview) {
        this.personaResources.upsertPersonaUserAnswer(systemAnswerId, personaQuestionAndAnswerTypeWithDetails, modalResponse)
    .subscribe(x => {
      let personaUserAnswerUpdates: { [key: number]: number } = {};

      let newUserSystemAnswerId = x.userSystemAnswerId?.personaUserAnswerId
        ?? x.userSystemAnswerId?.personaUserNonprofitAnswerId
        ?? x.userSystemAnswerId?.personaUserStagedNonprofitAnswerId;

      personaUserAnswerUpdates[newUserSystemAnswerId] = systemAnswerId;

      personaUserAnswers
        ?.systemAnswers
        ?.filter(x => x.systemAnswerId != systemAnswerId)
        ?.forEach(x => {
          if (x.userSystemAnswerId && x.systemAnswerId) {
            personaUserAnswerUpdates[x.userSystemAnswerId] = x.systemAnswerId;
          }
        });

      var updatePersonaQuestionsAndAnswersDataInfo: PersonaTypings.UpdatePersonaQuestionsAndAnswersDataInfo = {
        systemQuestionId,
        systemAnswerInfos: personaUserAnswerUpdates,
        customResponse: modalResponse,
        userSystemAnswerIdForCustomResponse: newUserSystemAnswerId,
        systemAnswerIdForCustomResponse: systemAnswerId,
        defaultIfNoAnswer: false
      };

      this.updatePersonaQuestionsAndAnswersData(updatePersonaQuestionsAndAnswersDataInfo);
    });
  }

  private onAnswerUpdate_PUT (
    userSystemAnswerId: number,
    systemAnswerId: number,
    personaQuestionsAndAnswers: PersonaTypings.PersonaQuestionAndAnswers,
    personaQuestionAndAnswerTypeWithDetails: PersonaTypings.PersonaQuestionAndAnswerTypeWithDetails,
    allowEmptySelection: boolean = false,
    modalResponse: string = null,
    personaUserAnswers: PersonaTypings.PersonaUserAnswers
      | PersonaTypings.PersonaUserNonprofitAnswers
      | PersonaTypings.PersonaUserStagedNonprofitAnswers
      | PersonaTypings.PersonaUserNonprofitAnswersForReview
      | PersonaTypings.PersonaUserStagedNonprofitAnswersForReview
  ) {
    this.personaResources.upsertPersonaUserAnswer(systemAnswerId, personaQuestionAndAnswerTypeWithDetails, modalResponse, userSystemAnswerId)
    .subscribe(() => {
      let personaUserAnswerUpdate: { [key: number]: number } = {};

      if (!personaQuestionsAndAnswers.allowsMultipleResponses) {
        personaUserAnswerUpdate[userSystemAnswerId] = systemAnswerId;
      } else {
        personaUserAnswers
          ?.systemAnswers
          ?.forEach(x => {
            if (x.userSystemAnswerId && x.systemAnswerId) {
              personaUserAnswerUpdate[x.userSystemAnswerId] = x.systemAnswerId;
            }
          });
      }

      if (modalResponse) {
        var updatePersonaQuestionsAndAnswersDataInfo: PersonaTypings.UpdatePersonaQuestionsAndAnswersDataInfo = {
          systemQuestionId: personaQuestionsAndAnswers.systemQuestionId,
          systemAnswerInfos: personaUserAnswerUpdate,
          customResponse: modalResponse,
          userSystemAnswerIdForCustomResponse: userSystemAnswerId,
          systemAnswerIdForCustomResponse: systemAnswerId,
          defaultIfNoAnswer: false
        };

        this.updatePersonaQuestionsAndAnswersData(updatePersonaQuestionsAndAnswersDataInfo);
      } else {
          var updatePersonaQuestionsAndAnswersDataInfo: PersonaTypings.UpdatePersonaQuestionsAndAnswersDataInfo = {
            systemQuestionId: personaQuestionsAndAnswers.systemQuestionId,
            systemAnswerInfos: personaUserAnswerUpdate,
            customResponse: null,
            userSystemAnswerIdForCustomResponse: null,
            systemAnswerIdForCustomResponse: null,
            defaultIfNoAnswer: false
          };

        this.updatePersonaQuestionsAndAnswersData(updatePersonaQuestionsAndAnswersDataInfo);
      }
    });
  }

  private onAnswerUpdate_POST_SingleResponseQuestion (
    systemAnswerId: number,
    personaQuestionsAndAnswers: PersonaTypings.PersonaQuestionAndAnswers,
    personaQuestionAndAnswerTypeWithDetails: PersonaTypings.PersonaQuestionAndAnswerTypeWithDetails,
    allowEmptySelection: boolean = false,
    modalResponse: string = null
  ) {
    return this.personaResources.upsertPersonaUserAnswer(systemAnswerId, personaQuestionAndAnswerTypeWithDetails, modalResponse)
      .subscribe((x) => {
        var newPersonaUserAnswerId = (x?.userSystemAnswerId?.personaUserAnswerId ??
          x?.userSystemAnswerId?.personaUserNonprofitAnswerId ??
          x?.userSystemAnswerId?.personaUserStagedNonprofitAnswerId);

        let personaUserAnswerUpdate: { [key: number]: number } = {};

        personaUserAnswerUpdate[newPersonaUserAnswerId] = systemAnswerId;

        if (modalResponse) {
          var updatePersonaQuestionsAndAnswersDataInfo: PersonaTypings.UpdatePersonaQuestionsAndAnswersDataInfo = {
            systemQuestionId: personaQuestionsAndAnswers.systemQuestionId,
            systemAnswerInfos: personaUserAnswerUpdate,
            customResponse: modalResponse,
            userSystemAnswerIdForCustomResponse: newPersonaUserAnswerId,
            systemAnswerIdForCustomResponse: systemAnswerId,
            defaultIfNoAnswer: false
          };

          this.updatePersonaQuestionsAndAnswersData(updatePersonaQuestionsAndAnswersDataInfo);
        } else {
          var updatePersonaQuestionsAndAnswersDataInfo: PersonaTypings.UpdatePersonaQuestionsAndAnswersDataInfo = {
            systemQuestionId: personaQuestionsAndAnswers.systemQuestionId,
            systemAnswerInfos: personaUserAnswerUpdate,
            customResponse: null,
            userSystemAnswerIdForCustomResponse: null,
            systemAnswerIdForCustomResponse: null,
            defaultIfNoAnswer: false
          };

          this.updatePersonaQuestionsAndAnswersData(updatePersonaQuestionsAndAnswersDataInfo);
        }
      });
  }

  private async handleCustomResponsePersonaUserAnswer_ForSingleResponse (
    systemAnswerId: number,
    personaQuestionAndAnswerTypeWithDetails: PersonaTypings.PersonaQuestionAndAnswerTypeWithDetails,
    userSystemAnswerId?: number,
    customResponse?: string,
    existingSystemAnswerId?: number,
    allowEmptySelection: boolean = false
  ) {
    let questionAndAnswerThatAllowsCustomResponse = this.personaQuestionsAndAnswersData
      .find(x => x.personaQuestionsAndAnswers.systemAnswers.find(x => x.systemAnswerId == systemAnswerId));

    const modalResponse = await this.modalFactory.open(
      AddEditPersonaAnswerCustomResponseModalComponent,
      {
        systemQuestion: questionAndAnswerThatAllowsCustomResponse.personaQuestionsAndAnswers.translationText,
        systemAnswer: questionAndAnswerThatAllowsCustomResponse.personaQuestionsAndAnswers.systemAnswers.find(x => x.systemAnswerId == systemAnswerId).translationText,
        customResponse
      },
      {
        class: 'modal-lg'
      }
    );

    if (!!modalResponse) {
      if (userSystemAnswerId) {
        this.onAnswerUpdate_PUT(
          userSystemAnswerId, systemAnswerId, questionAndAnswerThatAllowsCustomResponse.personaQuestionsAndAnswers, personaQuestionAndAnswerTypeWithDetails, allowEmptySelection, modalResponse, null
        );
      } else {
        this.onAnswerUpdate_POST_SingleResponseQuestion(
          systemAnswerId, questionAndAnswerThatAllowsCustomResponse.personaQuestionsAndAnswers, personaQuestionAndAnswerTypeWithDetails, allowEmptySelection, modalResponse
        );
      }
    } else {
      if (existingSystemAnswerId && userSystemAnswerId) {
        let personaUserAnswerUpdate: { [key: number]: number } = {};

        personaUserAnswerUpdate[userSystemAnswerId] = existingSystemAnswerId;

        var updatePersonaQuestionsAndAnswersDataInfo: PersonaTypings.UpdatePersonaQuestionsAndAnswersDataInfo = {
          systemQuestionId: questionAndAnswerThatAllowsCustomResponse.personaQuestionsAndAnswers.systemQuestionId,
          systemAnswerInfos: personaUserAnswerUpdate,
          customResponse: null,
          userSystemAnswerIdForCustomResponse: null,
          systemAnswerIdForCustomResponse: null,
          defaultIfNoAnswer: false
        };

        this.updatePersonaQuestionsAndAnswersData(updatePersonaQuestionsAndAnswersDataInfo);
      } else {
        var updatePersonaQuestionsAndAnswersDataInfo: PersonaTypings.UpdatePersonaQuestionsAndAnswersDataInfo = {
          systemQuestionId: questionAndAnswerThatAllowsCustomResponse.personaQuestionsAndAnswers.systemQuestionId,
          systemAnswerInfos: null,
          customResponse: null,
          userSystemAnswerIdForCustomResponse: null,
          systemAnswerIdForCustomResponse: null,
          defaultIfNoAnswer: false
        };

        this.updatePersonaQuestionsAndAnswersData(updatePersonaQuestionsAndAnswersDataInfo);
      }
    }
  }

  private async handlePersonaUserAnswer_ForSingleResponse (
    systemAnswerId: number,
    existingAnswer: PersonaTypings.PersonaUserAnswerSystemQuestionAnswers,
    personaQuestionsAndAnswers: PersonaTypings.PersonaQuestionAndAnswers,
    allowEmptySelection: boolean = false,
    personaQuestionAndAnswerTypeWithDetails: PersonaTypings.PersonaQuestionAndAnswerTypeWithDetails
  ) {
    if (existingAnswer?.userSystemAnswerId) {
      this.onAnswerUpdate_PUT(
        existingAnswer.userSystemAnswerId, systemAnswerId, personaQuestionsAndAnswers,
        personaQuestionAndAnswerTypeWithDetails, allowEmptySelection, null, null
      );
    } else {
      this.onAnswerUpdate_POST_SingleResponseQuestion(
        systemAnswerId, personaQuestionsAndAnswers, personaQuestionAndAnswerTypeWithDetails, allowEmptySelection
      );
    }
  }

  private async onAnswerUpdate_SingleResponseQuestion (
    answerId: number,
    personaQuestionsAndAnswers: PersonaTypings.PersonaQuestionAndAnswers,
    userAnswersToQuestion: PersonaTypings.PersonaUserAnswers | PersonaTypings.PersonaUserNonprofitAnswers | PersonaTypings.PersonaUserStagedNonprofitAnswers | PersonaTypings.PersonaUserNonprofitAnswersForReview | PersonaTypings.PersonaUserStagedNonprofitAnswersForReview,
    personaQuestionAndAnswerTypeWithDetails: PersonaTypings.PersonaQuestionAndAnswerTypeWithDetails,
    allowEmptySelection: boolean = false
  ) {
    let systemAnswerId = answerId;

    let existingAnswer = userAnswersToQuestion?.systemAnswers ?
      userAnswersToQuestion.systemAnswers[0] :
      null;

    let newAnswer = personaQuestionsAndAnswers?.systemAnswers?.find(x => x.systemAnswerId == answerId);

    if (newAnswer) {
      if (newAnswer.isCustomResponse) {
        await this.handleCustomResponsePersonaUserAnswer_ForSingleResponse(
          systemAnswerId,
          personaQuestionAndAnswerTypeWithDetails,
          existingAnswer?.userSystemAnswerId,
          null,
          existingAnswer?.systemAnswerId,
          allowEmptySelection
        );
      } else {
        await this.handlePersonaUserAnswer_ForSingleResponse(
          systemAnswerId,
          existingAnswer,
          personaQuestionsAndAnswers,
          allowEmptySelection,
          personaQuestionAndAnswerTypeWithDetails
        );
      }
    }
  }

  /**
   * handles removing a not custom response answer for a multi response question
   * @param removePersonaUserAnswerForMultiResponseQuestion
   */
  private handleRemovePersonaUserAnswer_ForMultiResponse_AnswersRemain (
    removePersonaUserAnswerForMultiResponseQuestion: PersonaTypings.RemovePersonaUserAnswerForMultiResponseQuestion
  ) {
    let existingAnswers = removePersonaUserAnswerForMultiResponseQuestion.existingAnswers;
    let incomingAnswerIds = removePersonaUserAnswerForMultiResponseQuestion.incomingAnswerIds;
    let personaQuestionAndAnswerTypeWithDetails = removePersonaUserAnswerForMultiResponseQuestion.personaQuestionAndAnswerTypeWithDetails;
    let personaQuestionsAndAnswers = removePersonaUserAnswerForMultiResponseQuestion.personaQuestionsAndAnswers;

    var answerToRemove = existingAnswers
    .filter(x => !incomingAnswerIds.includes(x.systemAnswerId))[0];

    this.personaResources.deletePersonaUserAnswer(answerToRemove.userSystemAnswerId, answerToRemove.systemAnswerId, personaQuestionAndAnswerTypeWithDetails)
      .subscribe(_ => {
        let personaUserAnswerUpdates: { [key: number]: number } = {};

        existingAnswers
          .filter(x => x.systemAnswerId != answerToRemove.systemAnswerId)
          ?.forEach(x => {
            if (x.userSystemAnswerId && x.systemAnswerId) {
              personaUserAnswerUpdates[x.userSystemAnswerId] = x.systemAnswerId;
            }
          });

        let indexOfCustomResponseAnswer = personaQuestionsAndAnswers
          ?.systemAnswers
          ?.findIndex(x => x.systemAnswerId == answerToRemove.systemAnswerId
            && x.isCustomResponse);

        var customResponseAnswerInfo = indexOfCustomResponseAnswer > -1 ?
          null :
          this.personaQuestionsAndAnswersData
            ?.find(x => x.personaQuestionsAndAnswers.systemQuestionId == personaQuestionsAndAnswers.systemQuestionId);

        if (customResponseAnswerInfo) {
          var updatePersonaQuestionsAndAnswersDataInfo: PersonaTypings.UpdatePersonaQuestionsAndAnswersDataInfo = {
            systemQuestionId: personaQuestionsAndAnswers.systemQuestionId,
            systemAnswerInfos: personaUserAnswerUpdates,
            customResponse: customResponseAnswerInfo.customResponseForQuestion,
            userSystemAnswerIdForCustomResponse: customResponseAnswerInfo.userSystemAnswerIdForCustomResponse,
            systemAnswerIdForCustomResponse: customResponseAnswerInfo.systemAnswerIdForCustomResponse,
            defaultIfNoAnswer: false
          };

          this.updatePersonaQuestionsAndAnswersData(updatePersonaQuestionsAndAnswersDataInfo);
        } else {
          var updatePersonaQuestionsAndAnswersDataInfo: PersonaTypings.UpdatePersonaQuestionsAndAnswersDataInfo = {
            systemQuestionId: personaQuestionsAndAnswers.systemQuestionId,
            systemAnswerInfos: personaUserAnswerUpdates,
            customResponse: null,
            userSystemAnswerIdForCustomResponse: null,
            systemAnswerIdForCustomResponse: null,
            defaultIfNoAnswer: false
          };

          this.updatePersonaQuestionsAndAnswersData(updatePersonaQuestionsAndAnswersDataInfo);
        }
      });
  }

  /**
   * handles removing an answer to a multi response question that results in there being no answers remaining.
   * if allowEmptySelection then no reversion to previous answer is taken.
   *
   * ensure Custom Response previous answer accounted for
   * @param existingAnswers
   * @param personaQuestionsAndAnswers
   * @param allowEmptySelection
   */
  private handleRemovePersonaUserAnswer_ForMultiResponse_NoAnswersRemain (
    existingAnswers: {
      systemAnswerId: number;
      userSystemAnswerId: number;
      userSystemAnswerCustomResponse: string;
    }[],
    personaQuestionsAndAnswers: PersonaTypings.PersonaQuestionAndAnswers,
    allowEmptySelection: boolean = false
  ) {
    let personaUserAnswerUpdates: { [key: number]: number } = {};

      existingAnswers
        ?.forEach(x => {
          if (x.userSystemAnswerId && x.systemAnswerId) {
            personaUserAnswerUpdates[x.userSystemAnswerId] = x.systemAnswerId;
          }
        });

      let indexOfCustomResponseAnswer = personaQuestionsAndAnswers
        ?.systemAnswers
        ?.findIndex(x => x.isCustomResponse);

      var customResponseAnswerInfo = indexOfCustomResponseAnswer > -1 ?
        this.personaQuestionsAndAnswersData
          ?.find(x => x.personaQuestionsAndAnswers.systemQuestionId == personaQuestionsAndAnswers.systemQuestionId) :
        null;

      if (customResponseAnswerInfo) {
        var updatePersonaQuestionsAndAnswersDataInfo: PersonaTypings.UpdatePersonaQuestionsAndAnswersDataInfo = {
          systemQuestionId: personaQuestionsAndAnswers.systemQuestionId,
          systemAnswerInfos: personaUserAnswerUpdates,
          customResponse: customResponseAnswerInfo.customResponseForQuestion,
          userSystemAnswerIdForCustomResponse: customResponseAnswerInfo.userSystemAnswerIdForCustomResponse,
          systemAnswerIdForCustomResponse: customResponseAnswerInfo.systemAnswerIdForCustomResponse,
          defaultIfNoAnswer: false
        };

        this.updatePersonaQuestionsAndAnswersData(updatePersonaQuestionsAndAnswersDataInfo);
      } else {
        var updatePersonaQuestionsAndAnswersDataInfo: PersonaTypings.UpdatePersonaQuestionsAndAnswersDataInfo = {
          systemQuestionId: personaQuestionsAndAnswers.systemQuestionId,
          systemAnswerInfos: personaUserAnswerUpdates,
          customResponse: null,
          userSystemAnswerIdForCustomResponse: null,
          systemAnswerIdForCustomResponse: null,
          defaultIfNoAnswer: false
        };

        this.updatePersonaQuestionsAndAnswersData(updatePersonaQuestionsAndAnswersDataInfo);
      }
  }

  /**
   * removes one answer from a multi response question
   * @param removePersonaUserAnswerForMultiResponseQuestion
   */
  private handleRemovePersonaUserAnswer_ForMultiResponse (
    removePersonaUserAnswerForMultiResponseQuestion: PersonaTypings.RemovePersonaUserAnswerForMultiResponseQuestion
  ) {
    let existingAnswers = removePersonaUserAnswerForMultiResponseQuestion.existingAnswers;
    let personaQuestionsAndAnswers = removePersonaUserAnswerForMultiResponseQuestion.personaQuestionsAndAnswers;
    let allowEmptySelection = true;

    if (existingAnswers.length > 1 || allowEmptySelection) {
      this.handleRemovePersonaUserAnswer_ForMultiResponse_AnswersRemain(removePersonaUserAnswerForMultiResponseQuestion);
    } else {
      this.handleRemovePersonaUserAnswer_ForMultiResponse_NoAnswersRemain(existingAnswers, personaQuestionsAndAnswers, allowEmptySelection);
    }
  }

  /**
   * adds one answer for a multi response question that is a custom response
   * @param answerToAdd
   * @param personaQuestionAndAnswerTypeWithDetails
   * @param existingAnswers
   * @param personaQuestionsAndAnswers
   * @param allowEmptySelection
   */
  private async handleAddPersonaUserAnswer_ForMultiResponse_CustomResponseAnswer (
    answerToAdd: number,
    personaQuestionAndAnswerTypeWithDetails: PersonaTypings.PersonaQuestionAndAnswerTypeWithDetails,
    existingAnswers: {
      systemAnswerId: number;
      userSystemAnswerId: number;
      userSystemAnswerCustomResponse: string;
    }[],
    personaQuestionsAndAnswers: PersonaTypings.PersonaQuestionAndAnswers,
    allowEmptySelection: boolean = false
  ) {
    const responseProvided = await this.addCustomResponsePersonaUserAnswer_ForMultiResponse(
      answerToAdd,
      personaQuestionAndAnswerTypeWithDetails
    );

    if (responseProvided) {
      responseProvided[1]
        .subscribe(x => {
          let personaUserAnswerUpdates: { [key: number]: number } = {};

          let newUserSystemAnswerId = x.userSystemAnswerId?.personaUserAnswerId
            ?? x.userSystemAnswerId?.personaUserNonprofitAnswerId
            ?? x.userSystemAnswerId?.personaUserStagedNonprofitAnswerId;

          personaUserAnswerUpdates[newUserSystemAnswerId] = x.systemAnswerId;

          existingAnswers
            .forEach(x => {
              if (x.userSystemAnswerId && x.systemAnswerId) {
                personaUserAnswerUpdates[x.userSystemAnswerId] = x.systemAnswerId;
              }
            });

          var updatePersonaQuestionsAndAnswersDataInfo: PersonaTypings.UpdatePersonaQuestionsAndAnswersDataInfo = {
            systemQuestionId: personaQuestionsAndAnswers.systemQuestionId,
            systemAnswerInfos: personaUserAnswerUpdates,
            customResponse: responseProvided[0],
            userSystemAnswerIdForCustomResponse: newUserSystemAnswerId,
            systemAnswerIdForCustomResponse: x.systemAnswerId,
            defaultIfNoAnswer: false
          };

          this.updatePersonaQuestionsAndAnswersData(updatePersonaQuestionsAndAnswersDataInfo);
        });
    } else {
      let personaUserAnswerUpdates: { [key: number]: number } = {};

      existingAnswers
        ?.forEach(x => {
          if (x.userSystemAnswerId && x.systemAnswerId) {
            personaUserAnswerUpdates[x.userSystemAnswerId] = x.systemAnswerId;
          }
        })

      var updatePersonaQuestionsAndAnswersDataInfo: PersonaTypings.UpdatePersonaQuestionsAndAnswersDataInfo = {
        systemQuestionId: personaQuestionsAndAnswers.systemQuestionId,
        systemAnswerInfos: personaUserAnswerUpdates,
        customResponse: null,
        userSystemAnswerIdForCustomResponse: null,
        systemAnswerIdForCustomResponse: null,
        defaultIfNoAnswer: false
      };

      this.updatePersonaQuestionsAndAnswersData(updatePersonaQuestionsAndAnswersDataInfo);
    }
  }

  /**
   * adds one answer for a multi response question that is not a custom response
   * @param answerToAdd
   * @param personaQuestionAndAnswerTypeWithDetails
   * @param existingAnswers
   * @param personaQuestionsAndAnswers
   * @param allowEmptySelection
   */
  private async handleAddPersonaUserAnswer_ForMultiResponse_NotCustomResponseAnswer (
    answerToAdd: number,
    personaQuestionAndAnswerTypeWithDetails: PersonaTypings.PersonaQuestionAndAnswerTypeWithDetails,
    existingAnswers: {
      systemAnswerId: number;
      userSystemAnswerId: number;
      userSystemAnswerCustomResponse: string;
    }[],
    personaQuestionsAndAnswers: PersonaTypings.PersonaQuestionAndAnswers,
    allowEmptySelection: boolean = false
  ) {
    return this.personaResources.upsertPersonaUserAnswer(answerToAdd, personaQuestionAndAnswerTypeWithDetails, null)
      .subscribe(x => {
        let personaUserAnswerUpdates: { [key: number]: number } = {};

        let newUserSystemAnswerId = x.userSystemAnswerId?.personaUserAnswerId
          ?? x.userSystemAnswerId?.personaUserNonprofitAnswerId
          ?? x.userSystemAnswerId?.personaUserStagedNonprofitAnswerId;

        personaUserAnswerUpdates[newUserSystemAnswerId] = x.systemAnswerId;

        existingAnswers
          .forEach(x => {
            if (x.userSystemAnswerId && x.systemAnswerId) {
              personaUserAnswerUpdates[x.userSystemAnswerId] = x.systemAnswerId;
            }
          })

        let customResponseAnswerInfo = this.personaQuestionsAndAnswersData
          ?.find(x => x.personaQuestionsAndAnswers.systemQuestionId == personaQuestionsAndAnswers.systemQuestionId);

        var updatePersonaQuestionsAndAnswersDataInfo: PersonaTypings.UpdatePersonaQuestionsAndAnswersDataInfo = {
          systemQuestionId: personaQuestionsAndAnswers.systemQuestionId,
          systemAnswerInfos: personaUserAnswerUpdates,
          customResponse: customResponseAnswerInfo.customResponseForQuestion,
          userSystemAnswerIdForCustomResponse: customResponseAnswerInfo.userSystemAnswerIdForCustomResponse,
          systemAnswerIdForCustomResponse: customResponseAnswerInfo.systemAnswerIdForCustomResponse,
          defaultIfNoAnswer: false
        };

        this.updatePersonaQuestionsAndAnswersData(updatePersonaQuestionsAndAnswersDataInfo);
      });
  }

  /**
   * adds one answer for a multi response question
   * @param removePersonaUserAnswerForMultiResponseQuestion
   */
  private async handleAddPersonaUserAnswer_ForMultiResponse (
    removePersonaUserAnswerForMultiResponseQuestion: PersonaTypings.RemovePersonaUserAnswerForMultiResponseQuestion
  ) {
    let existingAnswers = removePersonaUserAnswerForMultiResponseQuestion.existingAnswers;
    let incomingAnswerIds = removePersonaUserAnswerForMultiResponseQuestion.incomingAnswerIds;
    let personaQuestionAndAnswerTypeWithDetails = removePersonaUserAnswerForMultiResponseQuestion.personaQuestionAndAnswerTypeWithDetails;
    let personaQuestionsAndAnswers = removePersonaUserAnswerForMultiResponseQuestion.personaQuestionsAndAnswers;
    let allowEmptySelection = true;

    var answerToAdd = incomingAnswerIds
      .filter(x => !existingAnswers
        .map(x => x.systemAnswerId)
        .includes(x))[0];

    let indexOfCustomResponseAnswer = personaQuestionsAndAnswers
      ?.systemAnswers
      ?.findIndex(x => x.systemAnswerId == answerToAdd
        && x.isCustomResponse);

    if (indexOfCustomResponseAnswer > -1) {
      await this.handleAddPersonaUserAnswer_ForMultiResponse_CustomResponseAnswer(answerToAdd, personaQuestionAndAnswerTypeWithDetails, existingAnswers, personaQuestionsAndAnswers, allowEmptySelection);
    } else {
      await this.handleAddPersonaUserAnswer_ForMultiResponse_NotCustomResponseAnswer(answerToAdd, personaQuestionAndAnswerTypeWithDetails, existingAnswers, personaQuestionsAndAnswers, allowEmptySelection);
    }
  }

  /**
   * sets the initial answers for a user to a multi response question
   * @param removePersonaUserAnswerForMultiResponseQuestion
   */
  private async handleInitialPersonaUserAnswers_ForMultiResponse (
    removePersonaUserAnswerForMultiResponseQuestion: PersonaTypings.RemovePersonaUserAnswerForMultiResponseQuestion
  ) {
    let incomingAnswerIds = removePersonaUserAnswerForMultiResponseQuestion.incomingAnswerIds;
    let personaQuestionAndAnswerTypeWithDetails = removePersonaUserAnswerForMultiResponseQuestion.personaQuestionAndAnswerTypeWithDetails;
    let personaQuestionsAndAnswers = removePersonaUserAnswerForMultiResponseQuestion.personaQuestionsAndAnswers;

    let personaUserAnswersToAdd: Observable<
      {
        userSystemAnswerId: PersonaTypings.NewPersonaAnswer;
        systemAnswerId: number;
      }>[] = [];

    let customResponseToQuestion: string = null;

    let indexOfCustomResponseAnswer = personaQuestionsAndAnswers
      ?.systemAnswers
      ?.findIndex(x => x.isCustomResponse);

    if (indexOfCustomResponseAnswer > -1) {
      var customResponseQuestionInfo = personaQuestionsAndAnswers.systemAnswers[indexOfCustomResponseAnswer];

      if (incomingAnswerIds.includes(customResponseQuestionInfo.systemAnswerId)) {
        const responseProvided = await this.addCustomResponsePersonaUserAnswer_ForMultiResponse(
          customResponseQuestionInfo.systemAnswerId,
          personaQuestionAndAnswerTypeWithDetails
        );

        if (responseProvided) {
          customResponseToQuestion = responseProvided[0];

          personaUserAnswersToAdd.push(responseProvided[1]);
        }

        incomingAnswerIds = incomingAnswerIds.filter(x => x != customResponseQuestionInfo.systemAnswerId);
      }
    }

    var updatePersonaQuestionsAndAnswersDataInfo: PersonaTypings.UpdatePersonaQuestionsAndAnswersDataInfo = {
      systemQuestionId: personaQuestionsAndAnswers.systemQuestionId,
      systemAnswerInfos: null,
      customResponse: customResponseToQuestion,
      userSystemAnswerIdForCustomResponse: null,
      systemAnswerIdForCustomResponse: null,
      defaultIfNoAnswer: false
    };

    if (incomingAnswerIds.length == 0 && personaUserAnswersToAdd?.length == 0) {
      this.updatePersonaQuestionsAndAnswersData(updatePersonaQuestionsAndAnswersDataInfo);
    } else if (incomingAnswerIds.length == 0 && personaUserAnswersToAdd?.length == 1) {
      personaUserAnswersToAdd[0]
      .subscribe(x => {
        let personaUserAnswerUpdates: { [key: number]: number } = {};

        var newPersonaUserAnswerId = (x?.userSystemAnswerId?.personaUserAnswerId ??
          x?.userSystemAnswerId?.personaUserNonprofitAnswerId ??
          x?.userSystemAnswerId?.personaUserStagedNonprofitAnswerId);

        personaUserAnswerUpdates[newPersonaUserAnswerId] = x.systemAnswerId;

        updatePersonaQuestionsAndAnswersDataInfo.userSystemAnswerIdForCustomResponse = newPersonaUserAnswerId;
        updatePersonaQuestionsAndAnswersDataInfo.systemAnswerIdForCustomResponse = x.systemAnswerId;
        updatePersonaQuestionsAndAnswersDataInfo.systemAnswerInfos = personaUserAnswerUpdates;

        this.updatePersonaQuestionsAndAnswersData(updatePersonaQuestionsAndAnswersDataInfo);
      });
    } else {
      incomingAnswerIds.forEach(async incomingAnswerId => {
        personaUserAnswersToAdd.push(
          this.personaResources.upsertPersonaUserAnswer(incomingAnswerId, personaQuestionAndAnswerTypeWithDetails, null)
        );
      })

      forkJoin(personaUserAnswersToAdd)
        .subscribe(x => {
          let personaUserAnswerUpdates: { [key: number]: number } = {};

          x.forEach(x => {
            var newPersonaUserAnswerId = (x?.userSystemAnswerId?.personaUserAnswerId ??
              x?.userSystemAnswerId?.personaUserNonprofitAnswerId ??
              x?.userSystemAnswerId?.personaUserStagedNonprofitAnswerId);

            personaUserAnswerUpdates[newPersonaUserAnswerId] = x.systemAnswerId;
          })

          updatePersonaQuestionsAndAnswersDataInfo.systemAnswerInfos = personaUserAnswerUpdates;

          this.updatePersonaQuestionsAndAnswersData(updatePersonaQuestionsAndAnswersDataInfo);
        });
    }
  }

  /**
   * handles updates to multi response questions; either removes one answer, adds one answer, or sets the initial answers for a user
   * @param incomingAnswerIds
   * @param personaQuestionsAndAnswers
   * @param userAnswersToQuestion
   * @param personaQuestionAndAnswerTypeWithDetails
   * @param allowEmptySelection
   */
  private async onAnswerUpdate_MultiResponseQuestion (
    incomingAnswerIds: number[],
    personaQuestionsAndAnswers: PersonaTypings.PersonaQuestionAndAnswers,
    userAnswersToQuestion: PersonaTypings.PersonaUserAnswers
      | PersonaTypings.PersonaUserNonprofitAnswers
      | PersonaTypings.PersonaUserStagedNonprofitAnswers
      | PersonaTypings.PersonaUserNonprofitAnswersForReview
      | PersonaTypings.PersonaUserStagedNonprofitAnswersForReview,
    personaQuestionAndAnswerTypeWithDetails: PersonaTypings.PersonaQuestionAndAnswerTypeWithDetails,
    allowEmptySelection: boolean = false
  ) {
    var existingAnswers = userAnswersToQuestion
      ?.systemAnswers
      ?.filter(x => x.userSystemAnswerId !== null
                && x.userSystemAnswerId !== undefined)
      ?.map((answer) => {
        return {
          systemAnswerId: answer.systemAnswerId,
          userSystemAnswerId: answer.userSystemAnswerId,
          userSystemAnswerCustomResponse: answer.userSystemAnswerCustomResponse
        }
      });

    let removePersonaUserAnswerForMultiResponseQuestion: PersonaTypings.RemovePersonaUserAnswerForMultiResponseQuestion = {
        existingAnswers,
        incomingAnswerIds,
        personaQuestionsAndAnswers,
        personaQuestionAndAnswerTypeWithDetails,
        allowEmptySelection
      };

    if (existingAnswers && incomingAnswerIds
      && ((incomingAnswerIds.length - existingAnswers.length === 1)
        || existingAnswers.length > incomingAnswerIds.length)
    ) {
      if (existingAnswers.length > incomingAnswerIds.length) {
        this.handleRemovePersonaUserAnswer_ForMultiResponse(removePersonaUserAnswerForMultiResponseQuestion);
      } else if (incomingAnswerIds.length - existingAnswers.length === 1) {
        await this.handleAddPersonaUserAnswer_ForMultiResponse(removePersonaUserAnswerForMultiResponseQuestion)
      }
    } else if ((!existingAnswers || existingAnswers.length == 0) && incomingAnswerIds) {
      await this.handleInitialPersonaUserAnswers_ForMultiResponse(removePersonaUserAnswerForMultiResponseQuestion);
    }
  }

}
export { PersonaQuestionAndAnswersForComponent };

